import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import {
  type Context,
  createContext,
  type Dispatch,
  type FC,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { auth } from "../firebase/firebaseConfig";
import { queryKeys } from "../queries/queryKeys";
import { esAdminPrincipal } from "../utils/adminAccess";
import {
  migrarHistorialGastosLegacyDeRuta,
  omitirHistorialGastosLegacy,
  tieneDocumentosEnGastosRuta,
  tieneHistorialGastosLegacyPendientes,
} from "../utils/rutaGastos";
import {
  calcularMetricasMovimientosDelDia,
  crearMovimientosRutaCollectionRef,
  type MovimientoRutaCompatible,
  obtenerTotalesRuta,
} from "../utils/rutaMovimientos";
import type {
  AdminUsersContextActions,
  AdminUsersContextState,
  AdminUsersContextValue,
  AnyObject,
  AppContextProps,
  AuthContextActions,
  AuthContextState,
  AuthContextValue,
  ClientesContextActions,
  ClientesContextState,
  ClientesContextValue,
  RouteDataRequirements,
  RutasContextActions,
  RutasContextState,
  RutasContextValue,
} from "./types";

export type {
  AdminUsersContextActions,
  AdminUsersContextState,
  AdminUsersContextValue,
  AnyObject,
  AppContextProps,
  AppContextValue,
  AuthContextActions,
  AuthContextState,
  AuthContextValue,
  ClientesContextActions,
  ClientesContextState,
  ClientesContextValue,
  RouteDataRequirements,
  RutasContextActions,
  RutasContextState,
  RutasContextValue,
} from "./types";

const RUTAS_CON_LISTA_ADMINS = new Set(["/", "/home"]);
const RUTAS_CON_USUARIO_RUTA = new Set(["/", "/home", "/gastos-diarios", "/estadisticas"]);
const RUTAS_CON_RUTAS_DATA = new Set(["/", "/home", "/estadisticas"]);
const RUTAS_CON_CLIENTES = new Set(["/", "/home", "/estadisticas"]);
const RUTA_ADMIN_UID_CACHE_PREFIX = "fast-admin:ruta-admin:";

const getFechaHoy = (): string => format(new Date(), "dd/MM/yyyy");

const getRutaAdminUidCacheKey = (currentUserUid: string): string =>
  `${RUTA_ADMIN_UID_CACHE_PREFIX}${currentUserUid}`;

const getCachedRutaAdminUid = (currentUserUid: string): string | null => {
  try {
    return window.sessionStorage.getItem(getRutaAdminUidCacheKey(currentUserUid));
  } catch {
    return null;
  }
};

const setCachedRutaAdminUid = (currentUserUid: string, adminUid: string): void => {
  try {
    window.sessionStorage.setItem(getRutaAdminUidCacheKey(currentUserUid), adminUid);
  } catch {
    // Ignore session storage failures and continue with Firebase-backed flows.
  }
};

const clearCachedRutaAdminUid = (currentUserUid: string): void => {
  try {
    window.sessionStorage.removeItem(getRutaAdminUidCacheKey(currentUserUid));
  } catch {
    // Ignore session storage failures and continue with Firebase-backed flows.
  }
};

const getMillisecondsUntilNextDay = (): number => {
  const nextDay = new Date();
  nextDay.setHours(24, 0, 0, 0);

  return Math.max(nextDay.getTime() - Date.now(), 1_000);
};

const normalizarNumero = (value: number | string | null | undefined): number => {
  const parsedValue = Number(value ?? 0);

  return Number.isNaN(parsedValue) ? 0 : parsedValue;
};

const esMismoDia = (fechaA: Date, fechaB: Date): boolean =>
  format(fechaA, "yyyy-MM-dd") === format(fechaB, "yyyy-MM-dd");

const normalizarFechaPago = (fecha: any): Date | null => {
  if (fecha instanceof Date) {
    return fecha;
  }

  if (typeof fecha?.toDate === "function") {
    return fecha.toDate();
  }

  const fechaConvertida = new Date(fecha);

  return Number.isNaN(fechaConvertida.getTime()) ? null : fechaConvertida;
};

const calcularAbonoDelDiaCliente = (cliente: AnyObject, fechaReferencia: Date): number => {
  const historialPagos: AnyObject[] = Array.isArray(cliente.historialPagos)
    ? cliente.historialPagos
    : [];

  return historialPagos.reduce((total, pago) => {
    const fechaPago = normalizarFechaPago(pago?.fecha);

    if (!fechaPago || !esMismoDia(fechaPago, fechaReferencia)) {
      return total;
    }

    return total + normalizarNumero(pago?.abono);
  }, 0);
};

const calcularMetricasClientes = (
  infoClientes: AnyObject[] | null,
  fechaHoy: string,
  fechaReferencia: Date,
): Pick<ClientesContextState, "prestamoDelDia" | "totalAbonos"> => {
  if (!infoClientes?.length) {
    return {
      prestamoDelDia: 0,
      totalAbonos: 0,
    };
  }

  return infoClientes.reduce<Pick<ClientesContextState, "prestamoDelDia" | "totalAbonos">>(
    (metricas, cliente) => {
      metricas.totalAbonos += calcularAbonoDelDiaCliente(cliente, fechaReferencia);

      if (cliente.fechaActual === fechaHoy) {
        metricas.prestamoDelDia += normalizarNumero(cliente.valorPrestamo);
      }

      return metricas;
    },
    {
      prestamoDelDia: 0,
      totalAbonos: 0,
    },
  );
};

const crearMetricasClientesIniciales = (): Pick<
  ClientesContextState,
  "prestamoDelDia" | "totalAbonos"
> => ({
  prestamoDelDia: 0,
  totalAbonos: 0,
});

const METRICAS_MOVIMIENTOS_INICIALES = crearMetricasClientesIniciales();

const authStateContext = createContext<AuthContextState | undefined>(undefined);
const authActionsContext = createContext<AuthContextActions | undefined>(undefined);
const adminUsersStateContext = createContext<AdminUsersContextState | undefined>(undefined);
const adminUsersActionsContext = createContext<AdminUsersContextActions | undefined>(undefined);
const rutasStateContext = createContext<RutasContextState | undefined>(undefined);
const rutasActionsContext = createContext<RutasContextActions | undefined>(undefined);
const clientesStateContext = createContext<ClientesContextState | undefined>(undefined);
const clientesActionsContext = createContext<ClientesContextActions | undefined>(undefined);

const useRequiredContext = <T,>(context: Context<T | undefined>, contextName: string): T => {
  const value = useContext(context);

  if (!value) {
    throw new Error(`${contextName} debe usarse dentro de AppContext`);
  }

  return value;
};

const useDataRequirements = (): RouteDataRequirements => {
  const { pathname } = useLocation();

  return useMemo(
    () => ({
      shouldLoadAdminUsersList: RUTAS_CON_LISTA_ADMINS.has(pathname),
      shouldLoadClientes: RUTAS_CON_CLIENTES.has(pathname),
      shouldLoadRutasData: RUTAS_CON_RUTAS_DATA.has(pathname),
      shouldLoadUsuarioRuta: RUTAS_CON_USUARIO_RUTA.has(pathname),
    }),
    [pathname],
  );
};

export const useAuthState = (): AuthContextState =>
  useRequiredContext(authStateContext, "useAuthState");

export const useAuthActions = (): AuthContextActions =>
  useRequiredContext(authActionsContext, "useAuthActions");

export const useAuthContext = (): AuthContextValue => {
  const state = useAuthState();
  const actions = useAuthActions();

  return useMemo(() => ({ ...state, ...actions }), [actions, state]);
};

export const useAdminUsersState = (): AdminUsersContextState =>
  useRequiredContext(adminUsersStateContext, "useAdminUsersState");

export const useAdminUsersActions = (): AdminUsersContextActions =>
  useRequiredContext(adminUsersActionsContext, "useAdminUsersActions");

export const useAdminUsersContext = (): AdminUsersContextValue => {
  const state = useAdminUsersState();
  const actions = useAdminUsersActions();

  return useMemo(() => ({ ...state, ...actions }), [actions, state]);
};

export const useRutasState = (): RutasContextState =>
  useRequiredContext(rutasStateContext, "useRutasState");

export const useRutasActions = (): RutasContextActions =>
  useRequiredContext(rutasActionsContext, "useRutasActions");

export const useRutasContext = (): RutasContextValue => {
  const state = useRutasState();
  const actions = useRutasActions();

  return useMemo(() => ({ ...state, ...actions }), [actions, state]);
};

export const useClientesState = (): ClientesContextState =>
  useRequiredContext(clientesStateContext, "useClientesState");

export const useClientesActions = (): ClientesContextActions =>
  useRequiredContext(clientesActionsContext, "useClientesActions");

export const useClientesContext = (): ClientesContextValue => {
  const state = useClientesState();
  const actions = useClientesActions();

  return useMemo(() => ({ ...state, ...actions }), [actions, state]);
};

const fetchAuthUserData = async (authUserUid: string): Promise<AnyObject | null> => {
  try {
    const db = getFirestore();
    const userSnapshot = await getDoc(doc(db, "admin_users", authUserUid));

    return userSnapshot.exists() ? (userSnapshot.data() as AnyObject) : null;
  } catch (error) {
    console.error("Error fetching current user data from Firebase:", error);
    return null;
  }
};

const AuthProvider: FC<AppContextProps> = ({ children }) => {
  const [authUserUid, setAuthUserUid] = useState<string | null>(null);
  const [isAuthResolved, setIsAuthResolved] = useState(false);
  const [isLogouting, setIsLogouting] = useState(false);
  const navigate = useNavigate();

  const handleLogout = useCallback(async () => {
    setIsLogouting(true);
    const currentUserUid = auth.currentUser?.uid;

    try {
      await auth.signOut();
      if (currentUserUid) {
        clearCachedRutaAdminUid(currentUserUid);
      }
      navigate("/", { replace: true });
      toast.success("Sesion cerrada exitosamente.");
    } catch (error) {
      console.error("Error al cerrar sesion:", error);
      toast.error("No se pudo cerrar sesion.");
    } finally {
      setIsLogouting(false);
    }
  }, [navigate]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((authUser) => {
      setAuthUserUid(authUser?.uid ?? null);
      setIsAuthResolved(true);
    });

    return unsubscribe;
  }, []);

  // El usuario actual solo cambia si el propio admin edita su perfil (accion
  // explicita, rara); staleTime/gcTime en Infinity porque no hay razon para
  // revalidar por tiempo un dato que ya es event-driven via onAuthStateChanged.
  const { data: userData = null, isSuccess: isUserDataFetched } = useQuery({
    enabled: !!authUserUid,
    gcTime: Number.POSITIVE_INFINITY,
    queryFn: () => fetchAuthUserData(authUserUid as string),
    queryKey: queryKeys.authUser(authUserUid ?? undefined),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: Number.POSITIVE_INFINITY,
  });

  // isSuccess (basado en el status de la query, no en isFetching) es la unica
  // senal segura de "ya tenemos el dato": isLoading puede quedar en false por
  // un render de mas justo cuando `enabled` pasa de false a true (login en
  // vivo dentro de la SPA, a diferencia de un refresh con sesion ya activa),
  // lo que dejaba isUserDataResolved en un estado inconsistente y AppRouter
  // con la pantalla en blanco tras iniciar sesion.
  const isUserDataResolved = authUserUid ? isAuthResolved && isUserDataFetched : isAuthResolved;

  const stateValue = useMemo(
    () => ({
      isLogouting,
      isUserDataResolved,
      userData,
    }),
    [isLogouting, isUserDataResolved, userData],
  );
  const actionsValue = useMemo(
    () => ({
      handleLogout,
    }),
    [handleLogout],
  );

  return (
    <authActionsContext.Provider value={actionsValue}>
      <authStateContext.Provider value={stateValue}>{children}</authStateContext.Provider>
    </authActionsContext.Provider>
  );
};

const fetchAdminUsersList = async (): Promise<AnyObject[]> => {
  const db = getFirestore();
  const querySnapshot = await getDocs(collection(db, "admin_users"));

  return Promise.all(
    querySnapshot.docs.map(async (adminDoc) => {
      const rutasSnapshot = await getDocs(collection(db, "admin_users", adminDoc.id, "rutas"));

      return {
        ...adminDoc.data(),
        nombresRutas: rutasSnapshot.docs
          .map((rutaDoc) => String(rutaDoc.data()?.nombreRuta ?? "").trim())
          .filter((nombreRuta) => nombreRuta.length > 0),
      };
    }),
  );
};

const AdminUsersProvider: FC<AppContextProps> = ({ children }) => {
  const { isUserDataResolved } = useAuthState();
  const { shouldLoadAdminUsersList } = useDataRequirements();
  const queryClient = useQueryClient();
  const currentUserEmail = auth.currentUser?.email ?? null;
  const canLoadAdminUsers =
    shouldLoadAdminUsersList && isUserDataResolved && esAdminPrincipal(currentUserEmail);

  // Cambia solo por 3 modales explicitos e infrecuentes (crear/editar/borrar
  // admin); staleTime largo evita relanzar el N+1 (una query extra por admin)
  // en cada navegacion, la invalidacion tras esas mutaciones es la via principal.
  const { data: usersAdminData = null } = useQuery({
    enabled: canLoadAdminUsers,
    gcTime: 30 * 60 * 1000,
    queryFn: fetchAdminUsersList,
    queryKey: queryKeys.adminUsersList(),
    staleTime: 15 * 60 * 1000,
  });

  // Se preserva la firma Dispatch<SetStateAction<...>> para no tener que
  // tocar los consumidores existentes (ModalEditAdmin, ModalDeleteAdmin,
  // ModalActualizarPago) en esta fase; por dentro escribe en la cache de
  // TanStack Query en vez de en un useState local.
  const setUsersAdminData: Dispatch<SetStateAction<AnyObject[] | null>> = useCallback(
    (value) => {
      queryClient.setQueryData<AnyObject[] | null>(queryKeys.adminUsersList(), (prev) =>
        typeof value === "function"
          ? (value as (prevState: AnyObject[] | null) => AnyObject[] | null)(prev ?? null)
          : value,
      );
    },
    [queryClient],
  );

  const stateValue = useMemo(
    () => ({
      usersAdminData,
    }),
    [usersAdminData],
  );
  const actionsValue = useMemo(
    () => ({
      setUsersAdminData,
    }),
    [setUsersAdminData],
  );

  return (
    <adminUsersActionsContext.Provider value={actionsValue}>
      <adminUsersStateContext.Provider value={stateValue}>
        {children}
      </adminUsersStateContext.Provider>
    </adminUsersActionsContext.Provider>
  );
};

const hidratarRutaSnapshot = async (
  db: ReturnType<typeof getFirestore>,
  adminUid: string,
  rutaSnapshot: AnyObject,
): Promise<AnyObject> => {
  const rutaBase = rutaSnapshot.data() as AnyObject;
  let rutaHidratada = rutaBase;

  try {
    const gastosRutaTieneDocumentos = await tieneDocumentosEnGastosRuta({
      adminUid,
      db,
      rutaUid: rutaSnapshot.id,
    });

    if (gastosRutaTieneDocumentos) {
      rutaHidratada = omitirHistorialGastosLegacy(rutaHidratada) as AnyObject;
    } else if (tieneHistorialGastosLegacyPendientes(rutaHidratada)) {
      rutaHidratada = await migrarHistorialGastosLegacyDeRuta({
        adminUid,
        db,
        rutaData: rutaHidratada,
        rutaUid: rutaSnapshot.id,
      });
    }
  } catch (error) {
    console.error(`Error migrating legacy historialGastos for ruta ${rutaSnapshot.id}:`, error);
  }

  return {
    ...(rutaHidratada as AnyObject),
    adminUid,
    uid: rutaSnapshot.id,
  };
};

const fetchRutasDeAdmin = async (adminUid: string): Promise<AnyObject[]> => {
  const db = getFirestore();
  const querySnapshot = await getDocs(collection(db, "admin_users", adminUid, "rutas"));
  const data: AnyObject[] = [];

  // Hydrate routes one by one to avoid firing multiple legacy migrations in parallel.
  for (const rutaDoc of querySnapshot.docs) {
    data.push(await hidratarRutaSnapshot(db, adminUid, rutaDoc));
  }

  return data;
};

const fetchUsuarioRutaDeSesion = async (currentUserUid: string): Promise<AnyObject | null> => {
  const db = getFirestore();
  let rutaPassDocCache: AnyObject | null | undefined;

  const getRutaPassDoc = async (): Promise<AnyObject | null> => {
    if (rutaPassDocCache !== undefined) {
      return rutaPassDocCache;
    }

    const rutasPassQuery = query(
      collection(db, "rutasPass"),
      where("uid", "==", currentUserUid),
      limit(1),
    );
    const rutasPassSnapshot = await getDocs(rutasPassQuery);

    rutaPassDocCache = rutasPassSnapshot.docs[0] ?? null;

    return rutaPassDocCache;
  };

  const fetchRutaByAdminUid = async (adminUid: string): Promise<AnyObject | null> => {
    const rutaSnapshot = await getDoc(doc(db, "admin_users", adminUid, "rutas", currentUserUid));

    if (!rutaSnapshot.exists()) {
      return null;
    }

    return hidratarRutaSnapshot(db, adminUid, rutaSnapshot);
  };

  const fetchUsuarioRutaFromPasswordIndex = async (): Promise<AnyObject | null> => {
    const rutaPassDoc = await getRutaPassDoc();
    const adminUid = rutaPassDoc?.data()?.adminUid;

    if (!adminUid) {
      return null;
    }

    return fetchRutaByAdminUid(adminUid);
  };

  const fetchUsuarioRutaFromCache = async (): Promise<AnyObject | null> => {
    const adminUid = getCachedRutaAdminUid(currentUserUid);

    if (!adminUid) {
      return null;
    }

    const matchedRuta = await fetchRutaByAdminUid(adminUid);

    if (!matchedRuta) {
      clearCachedRutaAdminUid(currentUserUid);
    }

    return matchedRuta;
  };

  const fetchUsuarioRutaFallback = async (): Promise<AnyObject | null> => {
    const rutaPassDoc = await getRutaPassDoc();
    const adminsSnapshot = await getDocs(collection(db, "admin_users"));
    const adminIds = adminsSnapshot.docs.map((adminDoc) => adminDoc.id);
    const rutasSnapshots = await Promise.all(
      adminIds.map(async (adminUid) => ({
        adminUid,
        ruta: await fetchRutaByAdminUid(adminUid),
      })),
    );
    const matchedRuta = rutasSnapshots.find((item) => item.ruta)?.ruta ?? null;

    if (matchedRuta && rutaPassDoc && rutaPassDoc.data()?.adminUid !== matchedRuta.adminUid) {
      try {
        await updateDoc(rutaPassDoc.ref, {
          adminUid: matchedRuta.adminUid,
        });
      } catch (error) {
        console.error("Error syncing adminUid in rutasPass:", error);
      }
    }

    return matchedRuta;
  };

  try {
    let matchedRuta: AnyObject | null = null;

    try {
      matchedRuta = await fetchUsuarioRutaFromCache();
    } catch (error) {
      console.error("Error fetching usuarioRuta from session cache:", error);
    }

    if (!matchedRuta) {
      try {
        matchedRuta = await fetchUsuarioRutaFromPasswordIndex();
      } catch (error) {
        console.error("Error fetching usuarioRuta from Firebase with rutasPass index:", error);
      }
    }

    if (!matchedRuta) {
      matchedRuta = await fetchUsuarioRutaFallback();
    }

    if (matchedRuta?.adminUid) {
      setCachedRutaAdminUid(currentUserUid, matchedRuta.adminUid);
    }

    return matchedRuta;
  } catch (error) {
    console.error("Error fetching usuarioRuta from Firebase:", error);
    return null;
  }
};

const RutasProvider: FC<AppContextProps> = ({ children }) => {
  const { isUserDataResolved, userData } = useAuthState();
  const { shouldLoadRutasData, shouldLoadUsuarioRuta } = useDataRequirements();
  const queryClient = useQueryClient();
  const currentUserUid = auth.currentUser?.uid;

  // Cambia solo por acciones administrativas explicitas e infrecuentes
  // (crear/editar/borrar ruta); staleTime es red de seguridad, la
  // invalidacion tras esas mutaciones es la via principal.
  const { data: rutasData = null } = useQuery({
    enabled: shouldLoadRutasData && isUserDataResolved && !!userData?.isAdmin && !!userData?.uid,
    gcTime: 20 * 60 * 1000,
    queryFn: () => fetchRutasDeAdmin(userData?.uid as string),
    queryKey: queryKeys.rutas(userData?.uid),
    staleTime: 5 * 60 * 1000,
  });

  // Identidad de sesion: efectivamente estatica hasta logout/login o cambio
  // de dispositivo. Complementa (no reemplaza) el sessionStorage ya existente
  // para "adminUid de la ruta", que sobrevive a un full reload de pagina.
  const { data: usuarioRuta = null } = useQuery({
    enabled: shouldLoadUsuarioRuta && isUserDataResolved && !!currentUserUid && !userData?.isAdmin,
    gcTime: Number.POSITIVE_INFINITY,
    queryFn: () => fetchUsuarioRutaDeSesion(currentUserUid as string),
    queryKey: queryKeys.usuarioRuta(currentUserUid),
    staleTime: Number.POSITIVE_INFINITY,
  });

  // Se preservan las firmas Dispatch<SetStateAction<...>> para no tener que
  // tocar los 20+ consumidores existentes en esta fase; por dentro escriben
  // en la cache de TanStack Query en vez de en un useState local.
  const setRutasData: Dispatch<SetStateAction<AnyObject[] | null>> = useCallback(
    (value) => {
      queryClient.setQueryData<AnyObject[] | null>(queryKeys.rutas(userData?.uid), (prev) =>
        typeof value === "function"
          ? (value as (prevState: AnyObject[] | null) => AnyObject[] | null)(prev ?? null)
          : value,
      );
    },
    [queryClient, userData?.uid],
  );

  const setUsuarioRuta: Dispatch<SetStateAction<AnyObject | null>> = useCallback(
    (value) => {
      queryClient.setQueryData<AnyObject | null>(queryKeys.usuarioRuta(currentUserUid), (prev) =>
        typeof value === "function"
          ? (value as (prevState: AnyObject | null) => AnyObject | null)(prev ?? null)
          : value,
      );
    },
    [queryClient, currentUserUid],
  );

  const { totalMonto1, totalMonto2 } = useMemo(() => {
    const { totalIngresos, totalSalidas } = obtenerTotalesRuta(usuarioRuta);

    return {
      totalMonto1: totalIngresos,
      totalMonto2: totalSalidas,
    };
  }, [usuarioRuta]);

  const stateValue = useMemo(
    () => ({
      rutasData,
      totalMonto1,
      totalMonto2,
      usuarioRuta,
    }),
    [rutasData, totalMonto1, totalMonto2, usuarioRuta],
  );
  const actionsValue = useMemo(
    () => ({
      setRutasData,
      setUsuarioRuta,
    }),
    [setRutasData, setUsuarioRuta],
  );

  return (
    <rutasActionsContext.Provider value={actionsValue}>
      <rutasStateContext.Provider value={stateValue}>{children}</rutasStateContext.Provider>
    </rutasActionsContext.Provider>
  );
};

const fetchClientesDeRuta = async (adminUid: string, rutaUid: string): Promise<AnyObject[]> => {
  const db = getFirestore();
  const querySnapshot = await getDocs(
    collection(db, "admin_users", adminUid, "rutas", rutaUid, "clientes"),
  );

  const data: AnyObject[] = querySnapshot.docs.map((clienteDoc) => ({
    uid: clienteDoc.id,
    ...(clienteDoc.data() as AnyObject),
  }));

  return data.sort((a, b) => {
    const posicionA = a.posicion !== undefined ? a.posicion : data.length - 1;
    const posicionB = b.posicion !== undefined ? b.posicion : data.length - 1;

    return posicionA - posicionB;
  });
};

const fetchMetricasMovimientosDelDia = async (
  adminUid: string,
  rutaUid: string,
): Promise<Pick<ClientesContextState, "prestamoDelDia" | "totalAbonos">> => {
  try {
    const db = getFirestore();
    const inicioDia = new Date();
    inicioDia.setHours(0, 0, 0, 0);
    const finDia = new Date(inicioDia);
    finDia.setDate(finDia.getDate() + 1);
    const movimientosSnapshot = await getDocs(
      query(
        crearMovimientosRutaCollectionRef(db, adminUid, rutaUid),
        where("fecha", ">=", inicioDia),
        where("fecha", "<", finDia),
      ),
    );
    const movimientos = movimientosSnapshot.docs.map(
      (movimientoDoc) => movimientoDoc.data() as MovimientoRutaCompatible,
    );

    return calcularMetricasMovimientosDelDia(movimientos, inicioDia);
  } catch (error) {
    console.error("Error fetching movimientos del dia from Firebase:", error);
    return crearMetricasClientesIniciales();
  }
};

const ClientesProvider: FC<AppContextProps> = ({ children }) => {
  const [fechaHoy, setFechaHoy] = useState(getFechaHoy);
  const { usuarioRuta } = useRutasState();
  const { shouldLoadClientes } = useDataRequirements();
  const queryClient = useQueryClient();
  const usuarioRutaAdminUid = usuarioRuta?.adminUid;
  const usuarioRutaUid = usuarioRuta?.uid;
  const puedeCargarClientes = shouldLoadClientes && !!usuarioRutaAdminUid && !!usuarioRutaUid;

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const scheduleNextUpdate = () => {
      timeoutId = setTimeout(() => {
        setFechaHoy(getFechaHoy());
        scheduleNextUpdate();
      }, getMillisecondsUntilNextDay());
    };

    setFechaHoy(getFechaHoy());
    scheduleNextUpdate();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  // Alta frecuencia (decenas de abonos/dia); refetchOnWindowFocus aprovecha
  // tanto el ciclo de foco en mobile como la navegacion interna del cobrador
  // entre pantallas, mitigando el riesgo de dos sesiones sobre la misma ruta.
  const { data: infoClientes = null } = useQuery({
    enabled: puedeCargarClientes,
    gcTime: 10 * 60 * 1000,
    queryFn: () => fetchClientesDeRuta(usuarioRutaAdminUid as string, usuarioRutaUid as string),
    queryKey: queryKeys.clientes(usuarioRutaAdminUid, usuarioRutaUid),
    refetchOnWindowFocus: true,
    staleTime: 2 * 60 * 1000,
  });

  // Alta frecuencia - cada abono/gasto/cierre lo altera y es dinero visible
  // en pantalla; la queryKey incluye fechaHoy, asi que el cruce de medianoche
  // dispara un fetch nuevo para la fecha correcta sin necesidad de forzarlo.
  const { data: metricasMovimientosDelDia = METRICAS_MOVIMIENTOS_INICIALES } = useQuery({
    enabled: puedeCargarClientes,
    gcTime: 5 * 60 * 1000,
    queryFn: () =>
      fetchMetricasMovimientosDelDia(usuarioRutaAdminUid as string, usuarioRutaUid as string),
    queryKey: queryKeys.movimientosDelDia(usuarioRutaAdminUid, usuarioRutaUid, fechaHoy),
    refetchOnWindowFocus: true,
    staleTime: 60 * 1000,
  });

  // Se preserva la firma Dispatch<SetStateAction<...>> para no tocar los
  // consumidores existentes en esta fase; escribe en la cache de TanStack
  // Query en vez de en un useState local.
  const setInfoClientes: Dispatch<SetStateAction<AnyObject[] | null>> = useCallback(
    (value) => {
      queryClient.setQueryData<AnyObject[] | null>(
        queryKeys.clientes(usuarioRutaAdminUid, usuarioRutaUid),
        (prev) =>
          typeof value === "function"
            ? (value as (prevState: AnyObject[] | null) => AnyObject[] | null)(prev ?? null)
            : value,
      );
    },
    [queryClient, usuarioRutaAdminUid, usuarioRutaUid],
  );

  const { prestamoDelDia, totalAbonos } = useMemo(() => {
    const metricasClientes = calcularMetricasClientes(infoClientes, fechaHoy, new Date());

    return {
      prestamoDelDia: metricasClientes.prestamoDelDia + metricasMovimientosDelDia.prestamoDelDia,
      totalAbonos: metricasClientes.totalAbonos + metricasMovimientosDelDia.totalAbonos,
    };
  }, [fechaHoy, infoClientes, metricasMovimientosDelDia]);

  const stateValue = useMemo(
    () => ({
      infoClientes,
      prestamoDelDia,
      totalAbonos,
    }),
    [infoClientes, prestamoDelDia, totalAbonos],
  );
  const actionsValue = useMemo(
    () => ({
      setInfoClientes,
    }),
    [setInfoClientes],
  );

  return (
    <clientesActionsContext.Provider value={actionsValue}>
      <clientesStateContext.Provider value={stateValue}>{children}</clientesStateContext.Provider>
    </clientesActionsContext.Provider>
  );
};

export const AppContext: FC<AppContextProps> = ({ children }) => {
  return (
    <AuthProvider>
      <AdminUsersProvider>
        <RutasProvider>
          <ClientesProvider>{children}</ClientesProvider>
        </RutasProvider>
      </AdminUsersProvider>
    </AuthProvider>
  );
};
